/*
  QUICK ADD TO CART — "Custom" quick_add mode

  Powers the overlay buttons (.qatc) rendered on product cards by
  snippets/card-product.liquid, plus the single page-wide variant picker
  (.vp) and mobile success toast (.qatc-toast) rendered once by
  snippets/quick-add-custom.liquid.

  Deliberately does NOT call cart-notification's renderContents() / open the
  cart drawer — the button's own success state + the mobile toast are the
  confirmation for this quick-add mode. It still updates the header cart
  icon bubble and publishes PUB_SUB_EVENTS.cartUpdate so the rest of the
  theme stays in sync.
*/
(function () {
  if (window.__qatcInitialized) return;
  window.__qatcInitialized = true;

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  }

  function init() {
    if (window.__qatcBound) return;
    window.__qatcBound = true;

    const picker = document.getElementById('quick-add-custom-picker');
    const toast = document.getElementById('quick-add-custom-toast');
    if (!picker || !toast) return;

    const strings = window.quickAddCustomStrings || {};
    const vp = (selector) => picker.querySelector(selector);

    // Markup for the selected-size checkmark badge (mobile only, see
    // .vp__opt-check in quick-add-custom.css) — read once from the
    // <template> in snippets/quick-add-custom.liquid rather than hardcoding
    // SVG markup in this file.
    const optCheckTpl = document.getElementById('qatc-opt-check-tpl');
    const optCheckIconHTML = optCheckTpl ? optCheckTpl.innerHTML : '';

    let toastTimer;
    let pickerState = null;
    let pickerLastFocus = null;

    // On mobile, products with many sizes only show the first 5 size boxes
    // up front, plus a "discover more" box (styled identically to a size
    // box) that reveals the rest in place when tapped. Desktop never hits
    // this since its modal is already capped to 3 size options.
    const MOBILE_VARIANT_LIMIT = 5;

    function showToast() {
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
    }

    function updateCartIconBubble(sections) {
      if (!sections || !sections['cart-icon-bubble']) return;
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const parsed = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (parsed) bubble.innerHTML = parsed.innerHTML;
    }

    function addToCart(variantId) {
      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      });

      return fetch(window.routes.cart_add_url, Object.assign(fetchConfig(), { body }))
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const error = new Error(response.description || response.message || 'Cart error');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'quick-add-custom',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            throw error;
          }
          updateCartIconBubble(response.sections);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-custom',
              productVariantId: variantId,
              cartData: response,
            });
          }
          return response;
        });
    }

    function getCardData(btn) {
      const container = btn.closest('.card__media') || btn.parentElement;
      const script = container && container.querySelector('[data-qatc-data]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    function setButtonAdded(btn) {
      btn.classList.add('is-added');
      btn.setAttribute('aria-label', strings.added || 'Added to cart');
      clearTimeout(btn._qatcTimer);
      btn._qatcTimer = setTimeout(() => {
        btn.classList.remove('is-added');
        btn.setAttribute('aria-label', strings.addToCart || 'Add to cart');
      }, 1800);
    }

    /* ---- variant picker ---- */
    function renderPicker() {
      const { variants, sel, added } = pickerState;
      const v = variants[sel];
      vp('[data-vp-price]').textContent = v.price;
      // Only the per-unit price (e.g. "44,33€/l") belongs next to the main price —
      // the selected variant's own size/bundle name (v.size) is already shown via
      // the highlighted option above, so it's intentionally left out here to avoid
      // redundantly repeating the bundle name (and its own price, when the
      // merchant includes it in the variant title) right next to the main price.
      vp('[data-vp-unit]').textContent = v.unit || '';

      // Label reads "In den Warenkorb" (strings.addToCart) in both states — only
      // the icon (bag-plus vs checkmark, swapped via .is-added in CSS) and button
      // color communicate idle vs. just-added; the price is already shown in
      // .vp__price above, so it's intentionally left out of the CTA label.
      vp('[data-vp-cta]').textContent = strings.addToCart || 'Add to cart';

      const ctaBtn = vp('[data-vp-add]');
      ctaBtn.classList.toggle('is-added', added);
      ctaBtn.disabled = !v.available;

      vp('[data-vp-opts]')
        .querySelectorAll('.vp__opt[role="radio"]')
        .forEach((optButton, i) => {
          optButton.setAttribute('aria-checked', i === sel ? 'true' : 'false');
          optButton.tabIndex = i === sel ? 0 : -1;
        });
    }

    // Returns the slice of pickerState.variants currently shown as boxes —
    // all of them, unless this is the mobile truncated view, in which case
    // just the first MOBILE_VARIANT_LIMIT (the rest are only reachable via
    // the "discover more" link through to the full PDP, not revealed here).
    // Because this is always a prefix slice, each shown variant's index here
    // is the same index it has in the full pickerState.variants array, so
    // data-i (and pickerState.sel, which indexes into the full array) stay
    // valid.
    function visibleVariants() {
      const { variants, isDesktop } = pickerState;
      if (isDesktop || variants.length <= MOBILE_VARIANT_LIMIT) return variants;
      return variants.slice(0, MOBILE_VARIANT_LIMIT);
    }

    function renderOpts() {
      const shown = visibleVariants();
      const isTruncated = shown.length < pickerState.variants.length;
      const optsEl = vp('[data-vp-opts]');

      // Desktop's .vp__opts grid reads this to size columns to the actual
      // number of options shown (see quick-add-custom.css) — e.g. a product
      // with only 2 sizes gets 2 wide columns instead of 2 narrow ones plus
      // empty space where a 3rd would have been.
      optsEl.style.setProperty('--vp-opt-count', shown.length);

      optsEl.innerHTML =
        shown
          .map(
            (v, i) => `
              <button type="button" role="radio" class="vp__opt" data-i="${i}"${v.available ? '' : ' disabled'}>
                <span class="vp__opt-label">
                  <span class="vp__opt-check" aria-hidden="true">${optCheckIconHTML}</span>
                  <span class="vp__opt-size"></span>
                </span>
                <span class="vp__opt-right"><span class="vp__opt-price"></span><span class="vp__opt-unit"></span></span>
              </button>`
          )
          .join('') +
        (isTruncated
          ? `<a class="vp__opt vp__opt--more" data-vp-more>
               <span class="vp__opt-size"></span>
             </a>`
          : '');

      // Fill text nodes via textContent (not innerHTML) to avoid any markup injection from variant titles.
      optsEl.querySelectorAll('.vp__opt[role="radio"]').forEach((optButton, i) => {
        optButton.querySelector('.vp__opt-size').textContent = shown[i].size || '';
        optButton.querySelector('.vp__opt-price').textContent = shown[i].price || '';
        optButton.querySelector('.vp__opt-unit').textContent = shown[i].unit || '';
      });

      if (isTruncated) {
        const moreEl = optsEl.querySelector('[data-vp-more]');
        moreEl.querySelector('.vp__opt-size').textContent = strings.discoverMore || 'Discover more';
        // Set via the href property (not interpolated into the innerHTML
        // template string above) so the product URL never needs HTML-escaping.
        moreEl.href = pickerState.data.productUrl || '#';
      }
    }

    function openPicker(trigger, data) {
      const allVariants = Array.isArray(data.variants) ? data.variants : [];
      if (!allVariants.length) return;

      // Out-of-stock sizes are hidden entirely rather than shown disabled —
      // shoppers only ever see sizes they can actually buy. If a product is
      // completely sold out (no variant available), fall back to showing
      // every variant (disabled) so the picker isn't left empty.
      const inStockVariants = allVariants.filter((v) => v.available);
      const sourceVariants = inStockVariants.length ? inStockVariants : allVariants;

      // Desktop modal shows at most the first 3 size options, to keep the
      // popup compact/consistent for products with many variants — extras
      // are simply not shown there. The mobile bottom sheet instead shows up
      // to MOBILE_VARIANT_LIMIT (5) up front and reveals the rest via the
      // "discover more" box — see renderOpts()/visibleVariants().
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      const variants = isDesktop ? sourceVariants.slice(0, 3) : sourceVariants;

      // With out-of-stock sizes filtered out above, every remaining variant
      // is normally available, so the first one is a sensible default. The
      // sold-out fallback case still needs the old search for the last
      // available (or, failing that, last) variant.
      let sel = 0;
      if (!inStockVariants.length) {
        sel = variants.length - 1;
        for (let i = variants.length - 1; i >= 0; i--) {
          if (variants[i].available) {
            sel = i;
            break;
          }
        }
      }

      pickerState = { data, variants, sel, added: false, isDesktop };
      pickerLastFocus = trigger;

      picker.querySelectorAll('[data-vp-img]').forEach((img) => {
        img.src = data.productImage || '';
        img.alt = data.productName || '';
      });
      vp('[data-vp-title]').textContent = data.productName || '';
      renderOpts();
      renderPicker();
      picker.classList.add('is-open');
      picker.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        picker.classList.add('is-visible');
        picker.addEventListener(
          'transitionend',
          () => {
            const activeOpt = vp('.vp__opt[aria-checked="true"]');
            trapFocus(picker, activeOpt || picker.querySelector('[role="dialog"]'));
          },
          { once: true }
        );
      });
    }

    function closePicker() {
      picker.classList.remove('is-visible');
      picker.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      removeTrapFocus(pickerLastFocus);
      setTimeout(() => picker.classList.remove('is-open'), 260);
      pickerState = null;
      // The picker is a single page-wide singleton reused for every product, so its
      // CTA button must be reset here on every close path (successful add, manual
      // close, Escape, backdrop click). Without this, a successful add leaves
      // aria-disabled="true" on the button permanently (the success .then() never
      // clears it), silently blocking every future click on the next product opened
      // in this same picker instance.
      vp('[data-vp-add]').removeAttribute('aria-disabled');
    }

    picker.addEventListener('click', (e) => {
      if (!pickerState) return;

      if (e.target.closest('[data-vp-close]')) {
        closePicker();
        return;
      }

      // [data-vp-more] is a plain <a href="..."> to the product's PDP (see
      // renderOpts()) — no special handling needed here, the browser's
      // default navigation takes over.

      const opt = e.target.closest('.vp__opt[role="radio"]');
      if (opt && !opt.disabled) {
        pickerState.sel = Number(opt.dataset.i);
        renderPicker();
        return;
      }

      if (e.target.closest('[data-vp-add]') && !pickerState.added) {
        const ctaBtn = vp('[data-vp-add]');
        if (ctaBtn.disabled || ctaBtn.getAttribute('aria-disabled') === 'true') return;

        const { variants, sel } = pickerState;
        const variant = variants[sel];
        ctaBtn.setAttribute('aria-disabled', 'true');

        addToCart(variant.id)
          .then(() => {
            if (!pickerState) return;
            pickerState.added = true;
            renderPicker();
            // Brief pause so the user can register the success checkmark before the
            // picker auto-closes. The checkmark swap and background-color change are
            // effectively instant (160ms transition), so this is dwell time only —
            // kept short since the cart/add request itself already adds real network
            // latency on top of this.
            setTimeout(() => {
              closePicker();
              showToast();
            }, 350);
          })
          .catch(() => {
            ctaBtn.removeAttribute('aria-disabled');
          });
      }
    });

    picker.addEventListener('keydown', (e) => {
      if (!pickerState) return;

      if (e.key === 'Escape') {
        closePicker();
        return;
      }

      if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key) && e.target.closest('.vp__opt[role="radio"]')) {
        e.preventDefault();
        // Cycle only among the radio boxes currently rendered (not the full
        // variants array), so arrow keys never land on a size hidden behind
        // the "discover more" link (those extra sizes are only reachable by
        // following that link to the full PDP).
        const n = visibleVariants().length;
        const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        pickerState.sel = (pickerState.sel + d + n) % n;
        renderPicker();
        const activeOpt = vp('.vp__opt[aria-checked="true"]');
        if (activeOpt) activeOpt.focus();
      }
    });

    /* ---- overlay quick-add button (event delegation so it works for cards rendered later, e.g. infinite scroll) ---- */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.qatc');
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      e.preventDefault();
      e.stopPropagation();

      const data = getCardData(btn);
      if (!data) return;

      if (btn.hasAttribute('data-has-variants')) {
        openPicker(btn, data);
        return;
      }

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.setAttribute('aria-disabled', 'true');
      addToCart(variantId)
        .then(() => {
          setButtonAdded(btn);
          showToast();
        })
        .finally(() => {
          btn.removeAttribute('aria-disabled');
        });
    });
  }
})();
